import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TaptapList } from 'sonata-react-components';

import { DealSelectionListStyled } from './DealSelectionList.styles';
import { ListSkeleton } from './ListSkeleton/ListSkeleton';

import './dealsSelectionList.scss';

import type { DealInterface, PlacementInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';

type Props = {
  dealPublishers?: Record<string, any>[];
  deals: DealInterface[];
  isLoading: boolean;
  onChangeSelection: (items: Record<string, any>[]) => void;
  selectedDeals: Record<string, any>;
  sites: Record<string, any>;
};

type DealListItem = {
  id: string;
  name: string;
  appAndSite: string;
  size: string;
  publisher: string;
  supplySource: string;
  sonataId: string | undefined;
};

export function DealsSelectionList({
  dealPublishers,
  deals,
  isLoading,
  onChangeSelection,
  selectedDeals,
  sites
}: Props) {
  const { formatMessage } = useIntl();

  const getAppAndSite = (placements: PlacementInterface[]) => {
    if (!placements || placements.length === 0) {
      return 'N/A';
    }
    return placements
      .map(placement => {
        const site = sites?.items?.find((s: Record<string, any>) => s._id === placement.site);
        if (site) {
          return `${site.name}-${site.platform === 'web' ? 'site' : 'app'}`;
        }
        return 'N/A';
      })
      .join(', ');
  };

  const getCreativeSize = (creatives: string[]) => {
    if (!creatives || creatives.length === 0) {
      return 'N/A';
    }
    return creatives
      .map((creat: string) =>
        formatMessage({
          defaultMessage: creat,
          id: `appAndSiteMarketplace.dealSelection.list.creativesSize.${creat}`
        })
      )
      .join(', ');
  };

  const formattedData = useMemo((): DealListItem[] => {
    return (
      deals?.map(item => {
        const publisherInfo = dealPublishers?.find(publisher => publisher.id === item._id);
        const dealId = item.origin.name === 'broadsign' ? item.origin.additionalInfo.external_id : item.origin.id;
        return {
          appAndSite: getAppAndSite(item.placements),
          id: dealId,
          name: item.alias || item.name,
          publisher: publisherInfo?.publisher || 'N/A',
          size: getCreativeSize(item.creatives),
          sonataId: item._id?.toString(),
          supplySource: item.origin.name.charAt(0).toUpperCase() + item.origin.name.slice(1)
        };
      }) ?? []
    );
  }, [deals, dealPublishers, sites]);

  // selectedDeals is keyed by index (position in the filtered list)
  const defaultSelectedItems = useMemo(
    () => formattedData.filter((_, index) => selectedDeals[index]),
    [formattedData, selectedDeals]
  );

  if (isLoading) {
    return <ListSkeleton />;
  }

  return (
    <DealSelectionListStyled className="dealsSelectionList">
      <TaptapList
        className="dealsSelectionList__table"
        data={formattedData}
        defaultSelectedItems={defaultSelectedItems}
        id="dealsSelectionList_table"
        onSelectionChange={onChangeSelection}
        selectionType="checkboxTop"
      >
        <TaptapList.Toolbar>
          <TaptapList.Toolbar.ShowSelected />
        </TaptapList.Toolbar>
        <TaptapList.ColumnsBar>
          <TaptapList.ColumnsBar.SelectAll />
          <TaptapList.ColumnsBar.Column
            className="dealsSelectionList__columnName"
            description={formatMessage({
              defaultMessage: 'ID',
              id: 'appAndSiteMarketplace.dealSelection.list.columns.id'
            })}
            id="deals-column-name"
            key="name"
          />
          <TaptapList.ColumnsBar.Column
            className="dealsSelectionList__columnAppAndSite"
            description={formatMessage({
              defaultMessage: 'App & Site',
              id: 'appAndSiteMarketplace.dealSelection.list.columns.appAndSite'
            })}
            id="deals-column-appAndSite"
            key="appAndSite"
          />
          <TaptapList.ColumnsBar.Column
            className="dealsSelectionList__columnSize"
            description={formatMessage({
              defaultMessage: 'Size',
              id: 'appAndSiteMarketplace.dealSelection.list.columns.size'
            })}
            id="deals-column-size"
            key="size"
          />
          <TaptapList.ColumnsBar.Column
            className="dealsSelectionList__columnPublisher"
            description={formatMessage({
              defaultMessage: 'Publisher',
              id: 'appAndSiteMarketplace.dealSelection.list.columns.publisher'
            })}
            id="deals-column-publisher"
            key="publisher"
          />
          <TaptapList.ColumnsBar.Column
            className="dealsSelectionList__columnSupplySource"
            description={formatMessage({
              defaultMessage: 'Supply Source',
              id: 'appAndSiteMarketplace.dealSelection.list.columns.supplySource'
            })}
            id="deals-column-supplySource"
            key="supplySource"
          />
        </TaptapList.ColumnsBar>

        <TaptapList.RowsList<DealListItem>
          emptyStateDescription={formatMessage({
            defaultMessage: "Try adjusting your search or filters to find what you're looking for.",
            id: 'appAndSiteMarketplace.dealSelection.list.emptyStateDescription'
          })}
          emptyStateIcon="fal fa-empty-set"
          emptyStateTitle={formatMessage({
            defaultMessage: 'No Results Found',
            id: 'appAndSiteMarketplace.dealSelection.list.emptyStateTitle'
          })}
        >
          {itemsList =>
            itemsList.map(item => (
              <TaptapList.RowsList.Row id={item.id} key={item.id}>
                <div className="dealsSelectionList__cellName" title={item.name}>
                  {item.name}
                </div>
                <div className="dealsSelectionList__cellAppAndSite" title={item.appAndSite}>
                  {item.appAndSite}
                </div>
                <div className="dealsSelectionList__cellSize" title={item.size}>
                  {item.size}
                </div>
                <div className="dealsSelectionList__cellPublisher" title={item.publisher}>
                  {item.publisher}
                </div>
                <div className="dealsSelectionList__cellSupplySource" title={item.supplySource}>
                  {item.supplySource}
                </div>
              </TaptapList.RowsList.Row>
            ))
          }
        </TaptapList.RowsList>
      </TaptapList>
    </DealSelectionListStyled>
  );
}
